import {
  Grid,
  useTheme,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import NavBar from '../components/NavBar';
import { useNavigate } from 'react-router';
import '../../index.css';
import { primaryColor, styles } from '../../assets/styles/styles';
import { Helmet } from 'react-helmet';
import { FadeInComponent } from '../components/FadeInComponent';
import { db } from '../../services/firebase_services';
import { getCurrentDateTime } from '../../services/home_services';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

export default function WelshAthleticsClaimForm() {
  const navigate = useNavigate();
  const theme = useTheme();

  const [scrollPosition, setScrollPosition] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [clubName, setClubName] = useState('');
  const [scheduleVisit, setScheduleVisit] = useState(false);
  const [preferredDateTime, setPreferredDateTime] = useState<Dayjs | null>(
    null
  );

  // Validation
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleScroll = () => {
    const position = window.scrollY;
    setScrollPosition(position);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!clubName.trim()) {
      newErrors.clubName = 'Run club name is required';
    }

    if (scheduleVisit && !preferredDateTime) {
      newErrors.preferredDateTime =
        'Please select a preferred date and time for the visit';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const dateTime = getCurrentDateTime();
      const requestData: any = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        clubName: clubName.trim(),
        scheduleVisit: scheduleVisit,
        dateTime,
      };

      if (scheduleVisit && preferredDateTime) {
        requestData.preferredVisitDateTime = preferredDateTime.toISOString();
        requestData.preferredVisitDateTimeFormatted = preferredDateTime.format(
          'MMMM DD, YYYY [at] hh:mm A'
        );
      }

      await db.ref('/new_data/welsh_athletics_request').push(requestData);

      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again.');
    }
  };

  if (submitted) {
    return (
      <>
        <Helmet>
          <meta name="title" content="Thank You | Welsh Athletics Club Claim" />
        </Helmet>

        <NavBar
          theme={theme}
          navigate={navigate}
          loggedIn={false}
          transparent={false}
        />

        <Grid
          container
          flexDirection={'column'}
          sx={{
            maxWidth: '100vw',
            backgroundColor: 'white',
            minHeight: '100vh',
          }}
        >
          <Grid
            container
            item
            xs={12}
            justifyContent={'center'}
            alignItems={'center'}
            sx={{
              paddingTop: 15,
              paddingBottom: 15,
            }}
          >
            <FadeInComponent scrollPosition={scrollPosition}>
              <Grid
                item
                container
                xs={10}
                md={6}
                justifyContent={'center'}
                alignItems={'center'}
                flexDirection={'column'}
              >
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: primaryColor,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: 30,
                  }}
                >
                  <Typography
                    style={{
                      color: 'white',
                      fontSize: 48,
                      fontWeight: '700',
                    }}
                  >
                    ✓
                  </Typography>
                </div>

                <Typography
                  component="h1"
                  variant="h3"
                  color={primaryColor}
                  sx={{
                    marginBottom: '20px',
                    fontFamily: 'Urbanist',
                    fontWeight: 900,
                    textAlign: 'center',
                  }}
                >
                  Thank You!
                </Typography>

                <Typography
                  variant={'body1'}
                  style={{
                    marginBottom: 20,
                    fontFamily: 'Urbanist',
                    fontSize: 20,
                    letterSpacing: 1.3,
                    color: 'rgba(40,40,40,1)',
                    textAlign: 'center',
                    lineHeight: 1.8,
                  }}
                >
                  We've received your club space claim request. Our team will
                  review your information and reach out to you soon.
                </Typography>

                <Typography
                  variant={'body1'}
                  style={{
                    marginBottom: 40,
                    fontFamily: 'Urbanist',
                    fontSize: 18,
                    letterSpacing: 1.3,
                    color: 'rgba(80,80,80,1)',
                    textAlign: 'center',
                  }}
                >
                  We're excited to have {clubName} join the RouteBuddies
                  community!
                </Typography>

                <Button
                  style={{
                    padding: 20,
                    borderRadius: '100px',
                    backgroundColor: primaryColor,
                    color: 'white',
                    paddingLeft: 40,
                    paddingRight: 40,
                  }}
                  onClick={() => {
                    navigate('/');
                  }}
                >
                  <Typography
                    style={{
                      color: 'white',
                      fontFamily: 'Urbanist',
                      fontWeight: '700',
                      textTransform: 'none',
                      fontSize: 18,
                    }}
                  >
                    Back to Home
                  </Typography>
                </Button>
              </Grid>
            </FadeInComponent>
          </Grid>
        </Grid>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <meta
          name="title"
          content="Claim Your Club Space | Welsh Athletics & RouteBuddies"
        />
        <meta
          name="description"
          content="Register your Welsh Athletics run club with RouteBuddies and create a safe space for your community."
        />
      </Helmet>

      <NavBar
        theme={theme}
        navigate={navigate}
        loggedIn={false}
        transparent={false}
      />

      <Grid
        container
        flexDirection={'column'}
        sx={{
          maxWidth: '100vw',
          backgroundColor: 'white',
        }}
      >
        {/* Form Section */}
        <Grid
          container
          item
          xs={12}
          justifyContent={'center'}
          sx={{
            paddingTop: 15,
            paddingBottom: 10,
            backgroundColor: 'white',
          }}
        >
          <FadeInComponent scrollPosition={scrollPosition}>
            <Grid item container xs={12} justifyContent={'center'}>
              <Grid
                container
                flexDirection={'column'}
                alignContent={'center'}
                xs={10}
                md={6}
              >
                <Typography
                  component="h1"
                  variant="h2"
                  color={primaryColor}
                  sx={{
                    marginBottom: '20px',
                    fontFamily: 'Urbanist',
                    ...styles.titleSize,
                    fontWeight: 900,
                    textAlign: 'center',
                  }}
                >
                  Claim Your Club Space
                </Typography>
                <div
                  style={{
                    width: '100%',
                    height: '10px',
                    backgroundColor: primaryColor,
                    marginTop: 20,
                    marginBottom: 30,
                  }}
                />
                <Typography
                  variant={'body1'}
                  style={{
                    marginBottom: 40,
                    fontFamily: 'Urbanist',
                    fontSize: 18,
                    letterSpacing: 1.3,
                    color: 'rgba(40,40,40,1)',
                    textAlign: 'center',
                    lineHeight: 1.8,
                  }}
                >
                  Fill out the form below to register your Welsh Athletics run
                  club with RouteBuddies. Our team will get back to you shortly!
                </Typography>

                {/* Form Fields */}
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      variant="outlined"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      InputProps={{
                        style: {
                          fontFamily: 'Urbanist',
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          fontFamily: 'Urbanist',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      variant="outlined"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      InputProps={{
                        style: {
                          fontFamily: 'Urbanist',
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          fontFamily: 'Urbanist',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      variant="outlined"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      error={!!errors.email}
                      helperText={errors.email}
                      InputProps={{
                        style: {
                          fontFamily: 'Urbanist',
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          fontFamily: 'Urbanist',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Run Club Name"
                      variant="outlined"
                      value={clubName}
                      onChange={e => setClubName(e.target.value)}
                      error={!!errors.clubName}
                      helperText={errors.clubName}
                      InputProps={{
                        style: {
                          fontFamily: 'Urbanist',
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          fontFamily: 'Urbanist',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={scheduleVisit}
                          onChange={e => {
                            setScheduleVisit(e.target.checked);
                            if (!e.target.checked) {
                              setPreferredDateTime(null);
                              setErrors({ ...errors, preferredDateTime: '' });
                            }
                          }}
                          sx={{
                            color: primaryColor,
                            '&.Mui-checked': {
                              color: primaryColor,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          style={{
                            fontFamily: 'Urbanist',
                            fontSize: 16,
                            color: 'rgba(40,40,40,1)',
                          }}
                        >
                          Would you like to schedule a club visit?
                        </Typography>
                      }
                    />
                  </Grid>

                  {scheduleVisit && (
                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          label="Preferred Visit Date & Time"
                          value={preferredDateTime}
                          onChange={newValue => setPreferredDateTime(newValue)}
                          minDateTime={dayjs()}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.preferredDateTime,
                              helperText: errors.preferredDateTime,
                              InputProps: {
                                style: {
                                  fontFamily: 'Urbanist',
                                },
                              },
                              InputLabelProps: {
                                style: {
                                  fontFamily: 'Urbanist',
                                },
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: primaryColor,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  )}

                  <Grid item xs={12} sx={{ marginTop: 2 }}>
                    <Button
                      fullWidth
                      style={{
                        padding: 20,
                        borderRadius: '100px',
                        backgroundColor: primaryColor,
                        color: 'white',
                      }}
                      onClick={handleSubmit}
                    >
                      <Typography
                        style={{
                          color: 'white',
                          fontFamily: 'Urbanist',
                          fontWeight: '700',
                          textTransform: 'none',
                          fontSize: 18,
                        }}
                      >
                        Submit Request
                      </Typography>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </FadeInComponent>
        </Grid>
      </Grid>
    </>
  );
}
